import { DEVICE_PRESENCE_POLL_INTERVAL_MS } from './constants';
import { isCurrentDevicePresent } from './devicePresence';

type DevicePresenceWatcherOptions = {
  deviceId: string | null;
  pollIntervalMs?: number;
  onPresenceChange: (present: boolean) => void;
};

/**
 * Reports when the recording microphone disappears from the device list and
 * when it comes back. Each transition is reported once; enumeration errors are
 * ignored so a flaky permissions API can never fake a lost device.
 */
export class DevicePresenceWatcher {
  private readonly deviceId: string | null;
  private readonly onPresenceChange: (present: boolean) => void;
  private readonly pollIntervalMs: number;
  private pollTimer: number | null = null;
  private present = true;
  private started = false;

  constructor({
    deviceId,
    onPresenceChange,
    pollIntervalMs = DEVICE_PRESENCE_POLL_INTERVAL_MS,
  }: DevicePresenceWatcherOptions) {
    this.deviceId = deviceId;
    this.onPresenceChange = onPresenceChange;
    this.pollIntervalMs = pollIntervalMs;
  }

  start(): void {
    if (this.started || !navigator.mediaDevices?.enumerateDevices) {
      return;
    }
    this.started = true;
    this.present = true;

    navigator.mediaDevices.addEventListener(
      'devicechange',
      this.handleDeviceChange,
    );
    this.pollTimer = window.setInterval(
      this.handleDeviceChange,
      this.pollIntervalMs,
    );
  }

  stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;

    navigator.mediaDevices.removeEventListener(
      'devicechange',
      this.handleDeviceChange,
    );
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private readonly handleDeviceChange = (): void => {
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        if (!this.started) {
          return;
        }
        const present = isCurrentDevicePresent(devices, this.deviceId);
        if (present !== this.present) {
          this.present = present;
          this.onPresenceChange(present);
        }
      })
      .catch(() => {
        /* ignore — enumeration failures must not look like a lost device */
      });
  };
}
